import { supabase } from '../../config/supabase';
import type {
  Boat,
  BoatWithRoster,
  BoatRosterEntry,
  BoatRosterEntryWithRower,
  BoatSeatRiggingRow,
  OarRigSide,
  CreateBoatPayload,
  UpdateBoatPayload,
  AssignBoatSeatPayload,
} from '../../types/boats';

/**
 * Service for boat management
 */
export const boatsService = {
  /**
   * Get all boats for a team
   * @param teamId Team ID
   * @returns Boats or error
   */
  async getTeamBoats(teamId: string): Promise<{ data?: Boat[]; error?: Error }> {
    try {
      const { data, error } = await supabase
        .from('boats')
        .select('*')
        .eq('team_id', teamId)
        .order('is_primary', { ascending: false })
        .order('created_at', { ascending: true });

      if (error) throw error;
      return { data: data as unknown as Boat[] };
    } catch (error) {
      console.error('[BoatsService] Error fetching team boats:', error);
      return {
        error: error instanceof Error ? error : new Error('Unexpected error fetching boats'),
      };
    }
  },

  /**
   * Get a single boat with its roster
   * @param boatId Boat ID
   * @returns Boat with roster or error
   */
  async getBoatWithRoster(boatId: string): Promise<{ data?: BoatWithRoster; error?: Error }> {
    try {
      const { data: boat, error: boatError } = await supabase
        .from('boats')
        .select('*')
        .eq('id', boatId)
        .single();

      if (boatError) throw boatError;

      const { data: roster, error: rosterError } = await supabase
        .from('boat_roster')
        .select('*')
        .eq('boat_id', boatId)
        .order('position', { ascending: true });

      if (rosterError) throw rosterError;

      return {
        data: {
          ...(boat as unknown as Boat),
          roster: (roster as unknown as BoatRosterEntry[]) || [],
        },
      };
    } catch (error) {
      console.error('[BoatsService] Error fetching boat with roster:', error);
      return {
        error: error instanceof Error ? error : new Error('Unexpected error fetching boat'),
      };
    }
  },

  /**
   * Get boat roster with rower details
   * @param boatId Boat ID
   * @returns Boat roster with rower details or error
   */
  async getBoatRosterWithRowers(
    boatId: string
  ): Promise<{ data?: BoatRosterEntryWithRower[]; error?: Error }> {
    try {
      const { data, error } = await supabase
        .from('boat_roster')
        .select(
          `
          *,
          rower:rower_profiles!boat_roster_rower_id_fkey (
            id,
            first_name,
            last_name,
            preferred_name
          )
        `
        )
        .eq('boat_id', boatId)
        .order('position', { ascending: true });

      if (error) throw error;
      return { data: data as unknown as BoatRosterEntryWithRower[] };
    } catch (error) {
      console.error('[BoatsService] Error fetching boat roster with rowers:', error);
      return {
        error: error instanceof Error ? error : new Error('Unexpected error fetching boat roster'),
      };
    }
  },

  /**
   * Create a new boat
   * @param payload Boat creation data
   * @returns Created boat or error
   */
  async createBoat(payload: CreateBoatPayload): Promise<{ data?: Boat; error?: Error }> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      // If setting as primary, unset other primary boats first
      if (payload.is_primary) {
        await supabase
          .from('boats')
          .update({ is_primary: false })
          .eq('team_id', payload.team_id)
          .eq('is_primary', true);
      }

      const { data, error } = await supabase
        .from('boats')
        .insert({
          ...(payload as unknown as Record<string, unknown>),
          ...(user?.id ? { created_by: user.id } : {}),
        })
        .select()
        .single();

      if (error) throw error;
      return { data: data as unknown as Boat };
    } catch (error) {
      console.error('[BoatsService] Error creating boat:', error);
      return {
        error: error instanceof Error ? error : new Error('Unexpected error creating boat'),
      };
    }
  },

  /**
   * Update a boat
   * @param boatId Boat ID
   * @param payload Boat update data
   * @returns Updated boat or error
   */
  async updateBoat(
    boatId: string,
    payload: UpdateBoatPayload
  ): Promise<{ data?: Boat; error?: Error }> {
    try {
      // If setting as primary, get team_id first to unset others
      if (payload.is_primary) {
        const { data: boat } = await supabase
          .from('boats')
          .select('team_id')
          .eq('id', boatId)
          .single();

        if (boat) {
          await supabase
            .from('boats')
            .update({ is_primary: false })
            .eq('team_id', boat.team_id)
            .eq('is_primary', true)
            .neq('id', boatId);
        }
      }

      const { data, error } = await supabase
        .from('boats')
        .update(payload as unknown as Record<string, unknown>)
        .eq('id', boatId)
        .select()
        .single();

      if (error) throw error;
      return { data: data as unknown as Boat };
    } catch (error) {
      console.error('[BoatsService] Error updating boat:', error);
      return {
        error: error instanceof Error ? error : new Error('Unexpected error updating boat'),
      };
    }
  },

  /**
   * Delete a boat (and cascade delete its roster entries)
   * @param boatId Boat ID
   * @returns Success or error
   */
  async deleteBoat(boatId: string): Promise<{ error?: Error }> {
    try {
      const { error } = await supabase.from('boats').delete().eq('id', boatId);

      if (error) throw error;
      return {};
    } catch (error) {
      console.error('[BoatsService] Error deleting boat:', error);
      return {
        error: error instanceof Error ? error : new Error('Unexpected error deleting boat'),
      };
    }
  },

  /**
   * Assign a rower to a boat seat
   * @param payload Assignment data
   * @returns Created roster entry or error
   */
  async assignBoatSeat(
    payload: AssignBoatSeatPayload
  ): Promise<{ data?: BoatRosterEntry; error?: Error }> {
    try {
      const { data, error } = await supabase
        .from('boat_roster')
        .upsert(
          {
            boat_id: payload.boat_id,
            rower_id: payload.rower_id,
            position: payload.position,
            side: payload.side || null,
            notes: payload.notes || null,
          },
          {
            onConflict: 'boat_id,position',
          }
        )
        .select()
        .single();

      if (error) throw error;
      return { data: data as unknown as BoatRosterEntry };
    } catch (error) {
      console.error('[BoatsService] Error assigning boat seat:', error);
      return {
        error: error instanceof Error ? error : new Error('Unexpected error assigning seat'),
      };
    }
  },

  /**
   * Remove a rower from a boat seat
   * @param boatId Boat ID
   * @param position Seat position
   * @returns Success or error
   */
  async unassignBoatSeat(boatId: string, position: number): Promise<{ error?: Error }> {
    try {
      const { error } = await supabase
        .from('boat_roster')
        .delete()
        .eq('boat_id', boatId)
        .eq('position', position);

      if (error) throw error;
      return {};
    } catch (error) {
      console.error('[BoatsService] Error unassigning boat seat:', error);
      return {
        error: error instanceof Error ? error : new Error('Unexpected error unassigning seat'),
      };
    }
  },

  /**
   * Clear all assignments for a boat
   * @param boatId Boat ID
   * @returns Success or error
   */
  async clearBoatRoster(boatId: string): Promise<{ error?: Error }> {
    try {
      const { error } = await supabase.from('boat_roster').delete().eq('boat_id', boatId);

      if (error) throw error;
      return {};
    } catch (error) {
      console.error('[BoatsService] Error clearing boat roster:', error);
      return {
        error: error instanceof Error ? error : new Error('Unexpected error clearing roster'),
      };
    }
  },

  /**
   * Peach Rig Info: per-seat oar side for a boat.
   */
  async listBoatSeatRigging(
    boatId: string
  ): Promise<{ data?: BoatSeatRiggingRow[]; error?: Error }> {
    try {
      const { data, error } = await supabase
        .from('boat_seat_rigging')
        .select('*')
        .eq('boat_id', boatId)
        .order('seat_index', { ascending: true });

      if (error) throw error;
      return { data: data as unknown as BoatSeatRiggingRow[] };
    } catch (error) {
      console.error('[BoatsService] Error listing boat seat rigging:', error);
      return {
        error: error instanceof Error ? error : new Error('Unexpected error loading rig layout'),
      };
    }
  },

  /**
   * Replace all Rig Info rows for a boat (ingestion and coach edits).
   */
  async replaceBoatSeatRigging(
    boatId: string,
    rows: Array<{ seat_index: number; oar_side: OarRigSide }>
  ): Promise<{ error?: Error }> {
    try {
      const { error: delErr } = await supabase
        .from('boat_seat_rigging')
        .delete()
        .eq('boat_id', boatId);
      if (delErr) throw delErr;

      if (rows.length === 0) return {};

      const insertRows = rows.map((r) => ({
        boat_id: boatId,
        seat_index: r.seat_index,
        oar_side: r.oar_side,
      }));

      const { error: insErr } = await supabase.from('boat_seat_rigging').insert(insertRows);
      if (insErr) throw insErr;
      return {};
    } catch (error) {
      console.error('[BoatsService] Error replacing boat seat rigging:', error);
      return {
        error: error instanceof Error ? error : new Error('Unexpected error saving rig layout'),
      };
    }
  },
};
